import cx from "classnames"
import { useStaticQuery, graphql } from "gatsby"
import PropTypes from "prop-types"
import { useMemo } from "react"
import BlogPostListingItem from "components/pages/blog/BlogPostListingItem"
import Section from "components/Layout/Section"
import SimpleHeader from "components/Layout/SimpleHeader"
import * as css from "./RelatedBlogPosts.module.css"

/**
 * RelatedBlogPosts - Displays blog posts by slug with proper blog card formatting
 *
 * Use this when you want to show actual blog cards (with hero image, author, category)
 * rather than generic cards with logos.
 */
function RelatedBlogPosts({ label, heading, subHeading, blogSlugs }) {
  // Query all blogs - we'll filter by slug client-side
  const data = useStaticQuery(graphql`
    {
      allMarkdownRemark(
        filter: { fields: { collection: { eq: "blog" } } }
        sort: { frontmatter: { date: DESC } }
      ) {
        nodes {
          fields {
            slug
          }
          frontmatter {
            title
            author
            category
            date
            thumbnail
            externalUrl
          }
          thumbnailImage {
            childImageSharp {
              gatsbyImageData(quality: 100, layout: FULL_WIDTH)
            }
          }
        }
      }
    }
  `)

  // Filter to only the blogs we want, in the order specified
  const blogs = useMemo(() => {
    const blogMap = new Map()

    data.allMarkdownRemark.nodes.forEach((blog) => {
      const slug = blog.fields?.slug
      if (slug) {
        blogMap.set(slug, {
          slug: blog.frontmatter.externalUrl || slug,
          title: blog.frontmatter.title,
          author: blog.frontmatter.author,
          category: blog.frontmatter.category,
          thumbnail: blog.thumbnailImage?.childImageSharp
            ? {
                gatsbyImageData:
                  blog.thumbnailImage.childImageSharp.gatsbyImageData,
              }
            : blog.frontmatter.thumbnail?.startsWith("http")
              ? { url: blog.frontmatter.thumbnail }
              : null,
        })
      }
    })

    // Return blogs in the order specified by blogSlugs
    return blogSlugs
      .map((slug) => {
        // Normalize slug - remove leading/trailing slashes for matching
        const normalizedSlug = slug.replace(/^\/blog\//, "").replace(/\/$/, "")
        const blog = blogMap.get(normalizedSlug)
        if (!blog && process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn(
            `[RelatedBlogPosts] Blog slug not found: "${slug}" (normalized: "${normalizedSlug}")`
          )
        }
        return blog
      })
      .filter(Boolean) // Remove any that weren't found
  }, [data, blogSlugs])

  if (blogs.length === 0) {
    return null
  }

  return (
    <Section padding="extraLight">
      <SimpleHeader
        label={label}
        title={heading}
        subtitle={subHeading}
        dash="vertical"
        narrow="both"
      />
      <div className={cx("grid grid-cols-1 md:grid-cols-3 gap-6", css.grid)}>
        {blogs.map((blog) => (
          <div key={blog.slug}>
            <BlogPostListingItem {...blog} />
          </div>
        ))}
      </div>
    </Section>
  )
}

RelatedBlogPosts.propTypes = {
  label: PropTypes.string,
  heading: PropTypes.string,
  subHeading: PropTypes.string,
  blogSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export default RelatedBlogPosts
